import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronLeft, FolderPlus, FolderTree, Pencil, Plus, Trash2 } from 'lucide-react'
import { categorySchema, formatNumber, PERMISSIONS, type CategoryNode } from '@najah/core'
import {
  Alert, Badge, Button, Card, ConfirmDialog, Dialog, EmptyState, Input, Select, Spinner, Switch, cn, useToast,
} from '@najah/ui'
import { categoriesApi, flattenCategories } from '../lib/catalogApi'
import { useAuth } from '../lib/auth'
import { useFormErrors } from '../lib/useFormErrors'

interface EditorState {
  id: string | null
  nameAr: string
  parentId: string
  sortOrder: string
  isActive: boolean
}

const EMPTY: EditorState = { id: null, nameAr: '', parentId: '', sortOrder: '0', isActive: true }

function CategoryRow({
  node, depth, canManage, onEdit, onAddChild, onDelete,
}: {
  node: CategoryNode
  depth: number
  canManage: boolean
  onEdit: (n: CategoryNode) => void
  onAddChild: (n: CategoryNode) => void
  onDelete: (n: CategoryNode) => void
}) {
  const [expanded, setExpanded] = useState(depth === 0)
  const hasChildren = node.children.length > 0

  return (
    <>
      <div
        className={cn(
          'group flex items-center gap-2 border-b border-border px-4 py-2.5 transition-colors hover:bg-surface-hover',
          !node.isActive && 'opacity-55',
        )}
        // المسافة البادئة منطقية لتنعكس في RTL
        style={{ paddingInlineStart: `${depth * 1.5 + 1}rem` }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="rounded p-0.5 text-fg-subtle transition-transform hover:text-fg"
            aria-expanded={expanded}
            aria-label={expanded ? 'طيّ' : 'توسيع'}
          >
            <ChevronLeft className={cn('size-4 transition-transform', expanded && '-rotate-90')} />
          </button>
        ) : (
          <span className="size-5" aria-hidden />
        )}

        <span className="flex-1 truncate text-sm font-medium text-fg">{node.nameAr}</span>

        {node.productCount > 0 && (
          <Badge tone="neutral" className="tabular-nums">{formatNumber(node.productCount)} منتج</Badge>
        )}
        {!node.isActive && <Badge tone="warning">مخفي</Badge>}

        {canManage && (
          <div className="flex gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
            <Button variant="ghost" size="icon" title="تصنيف فرعي" onClick={() => onAddChild(node)}>
              <FolderPlus className="size-4" />
            </Button>
            <Button variant="ghost" size="icon" title="تعديل" onClick={() => onEdit(node)}>
              <Pencil className="size-4" />
            </Button>
            <Button
              variant="ghost" size="icon" title="حذف"
              className="text-danger hover:bg-danger-subtle"
              onClick={() => onDelete(node)}
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
        )}
      </div>

      {expanded &&
        node.children.map((child) => (
          <CategoryRow
            key={child.id}
            node={child}
            depth={depth + 1}
            canManage={canManage}
            onEdit={onEdit}
            onAddChild={onAddChild}
            onDelete={onDelete}
          />
        ))}
    </>
  )
}

export function CategoriesPage() {
  const { can } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const canManage = can(PERMISSIONS.PRODUCT_MANAGE)

  const { data: tree, isPending } = useQuery({
    queryKey: ['categories', 'tree', 'all'],
    queryFn: () => categoriesApi.tree(true),
  })

  const [editorOpen, setEditorOpen] = useState(false)
  const [editor, setEditor] = useState<EditorState>(EMPTY)
  const [deleting, setDeleting] = useState<CategoryNode | null>(null)
  const { errors, formError, clear, validate, handleApiError } = useFormErrors()

  useEffect(() => { if (editorOpen) clear() }, [editorOpen, clear])

  const parentOptions = useMemo(() => {
    if (!tree) return []
    const all = flattenCategories(tree)
    // لا يمكن أن يكون التصنيف أباً لنفسه — نستبعده من الخيارات
    return editor.id ? all.filter((o) => o.value !== editor.id) : all
  }, [tree, editor.id])

  const saveMutation = useMutation({
    mutationFn: async () => {
      const parsed = validate(categorySchema, {
        nameAr: editor.nameAr.trim(),
        parentId: editor.parentId || null,
        sortOrder: Number(editor.sortOrder) || 0,
        isActive: editor.isActive,
      })
      if (!parsed) throw new Error('VALIDATION')
      return editor.id ? categoriesApi.update(editor.id, parsed) : categoriesApi.create(parsed)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['categories'] })
      toast.success(editor.id ? 'تم حفظ التصنيف' : 'تمت إضافة التصنيف')
      setEditorOpen(false)
    },
    onError: (err) => {
      if (err instanceof Error && err.message === 'VALIDATION') return
      handleApiError(err, 'تعذّر حفظ التصنيف')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => categoriesApi.remove(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['categories'] })
      toast.success('تم حذف التصنيف')
      setDeleting(null)
    },
    onError: (err) => {
      handleApiError(err)
      const message = err instanceof Error ? err.message : 'تعذّر حذف التصنيف'
      toast.error('تعذّر الحذف', message)
      setDeleting(null)
    },
  })

  function openCreate(parent?: CategoryNode) {
    setEditor({ ...EMPTY, parentId: parent?.id ?? '' })
    setEditorOpen(true)
  }

  function openEdit(node: CategoryNode) {
    setEditor({
      id: node.id,
      nameAr: node.nameAr,
      parentId: node.parentId ?? '',
      sortOrder: String(node.sortOrder),
      isActive: node.isActive,
    })
    setEditorOpen(true)
  }

  const totalCount = useMemo(() => {
    const count = (nodes: CategoryNode[]): number =>
      nodes.reduce((sum, n) => sum + 1 + count(n.children), 0)
    return tree ? count(tree) : 0
  }, [tree])

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-fg">التصنيفات</h1>
          <p className="mt-0.5 text-sm text-fg-muted">
            {isPending ? 'جارٍ التحميل…' : `${formatNumber(totalCount)} تصنيفاً`}
          </p>
        </div>
        {canManage && (
          <Button startIcon={<Plus className="size-4" />} onClick={() => openCreate()}>
            تصنيف جديد
          </Button>
        )}
      </div>

      <Card className="overflow-hidden">
        {isPending ? (
          <div className="grid place-items-center py-16"><Spinner className="size-6 text-primary" /></div>
        ) : tree && tree.length > 0 ? (
          <div>
            {tree.map((node) => (
              <CategoryRow
                key={node.id}
                node={node}
                depth={0}
                canManage={canManage}
                onEdit={openEdit}
                onAddChild={openCreate}
                onDelete={setDeleting}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            icon={<FolderTree className="size-10" />}
            title="لا توجد تصنيفات"
            description="نظّم منتجاتك في تصنيفات وتصنيفات فرعية"
            action={canManage ? <Button onClick={() => openCreate()}>إضافة تصنيف</Button> : undefined}
          />
        )}
      </Card>

      <Dialog
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        title={editor.id ? 'تعديل التصنيف' : 'تصنيف جديد'}
        dismissible={!saveMutation.isPending}
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditorOpen(false)} disabled={saveMutation.isPending}>
              إلغاء
            </Button>
            <Button onClick={() => saveMutation.mutate()} loading={saveMutation.isPending}>حفظ</Button>
          </>
        }
      >
        <div className="space-y-4">
          {formError && <Alert tone="danger">{formError}</Alert>}

          <Input
            label="اسم التصنيف" required autoFocus
            value={editor.nameAr}
            onChange={(e) => setEditor((s) => ({ ...s, nameAr: e.target.value }))}
            error={errors.nameAr}
          />

          <Select
            label="التصنيف الأب"
            value={editor.parentId}
            onChange={(e) => setEditor((s) => ({ ...s, parentId: e.target.value }))}
            options={parentOptions}
            placeholder="تصنيف رئيسي (بلا أب)"
            error={errors.parentId}
          />

          <Input
            label="ترتيب العرض" type="number" min="0" ltr
            value={editor.sortOrder}
            onChange={(e) => setEditor((s) => ({ ...s, sortOrder: e.target.value }))}
            error={errors.sortOrder}
            hint="الأصغر يظهر أولاً"
          />

          <Switch
            checked={editor.isActive}
            onChange={(e) => setEditor((s) => ({ ...s, isActive: e.target.checked }))}
            label="تصنيف ظاهر"
            description="التصنيفات المخفية لا تظهر في تطبيق الزبائن"
          />
        </div>
      </Dialog>

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && deleteMutation.mutate(deleting.id)}
        loading={deleteMutation.isPending}
        title="حذف التصنيف"
        confirmLabel="حذف"
        description={
          <>
            سيُحذف <span className="font-semibold text-fg">{deleting?.nameAr}</span>.
            <br />
            لا يمكن حذف تصنيف يحتوي منتجات أو تصنيفات فرعية.
          </>
        }
      />
    </div>
  )
}
